import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Radio, Wifi, WifiOff, Zap, Thermometer, MapPin, Battery,
  RefreshCw, Clock, AlertTriangle, ChevronDown, ChevronUp, Signal,
  Send, ArrowUpRight, Plug
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  success: 'bg-emerald-500',
  auth_failed: 'bg-amber-500',
  error: 'bg-red-500',
  not_started: 'bg-slate-400',
  disabled_missing_config: 'bg-slate-400',
};

const StatusDot = ({ status }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusColor[status] || 'bg-slate-400'} mr-2`} />
);

const SilicaData = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expandedDevice, setExpandedDevice] = useState(null);

  // Poller status
  const { data: pollerStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['silica-status'],
    queryFn: async () => (await axios.get(`${API}/silica/status`)).data,
    refetchInterval: 15000,
  });

  // Devices
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['silica-devices'],
    queryFn: async () => (await axios.get(`${API}/silica/devices`)).data,
    refetchInterval: 30000,
  });

  // Shock events
  const { data: shockData, isLoading: shocksLoading } = useQuery({
    queryKey: ['silica-shocks'],
    queryFn: async () => (await axios.get(`${API}/silica/shock-events?limit=20`)).data,
    refetchInterval: 30000,
  });

  // Recent telemetry
  const { data: telemetry = [], isLoading: telemetryLoading } = useQuery({
    queryKey: ['telemetry-recent'],
    queryFn: async () => (await axios.get(`${API}/telemetry?limit=30`)).data,
    refetchInterval: 10000,
  });

  // Recent alarms
  const { data: alarms = [], isLoading: alarmsLoading } = useQuery({
    queryKey: ['alarm-events'],
    queryFn: async () => (await axios.get(`${API}/alert-events?limit=20`)).data,
    refetchInterval: 15000,
  });

  // Poll now mutation
  const pollNow = useMutation({
    mutationFn: () => axios.post(`${API}/silica/poll-now`),
    onSuccess: () => {
      toast.success('Poll triggered');
      queryClient.invalidateQueries({ queryKey: ['silica-status'] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Poll failed'),
  });

  // ThingsBoard status
  const { data: tbStatus } = useQuery({
    queryKey: ['tb-status'],
    queryFn: async () => (await axios.get(`${API}/thingsboard/status`)).data,
    refetchInterval: 15000,
  });

  // TB test connection
  const tbTest = useMutation({
    mutationFn: () => axios.post(`${API}/thingsboard/test`),
    onSuccess: (res) => {
      const d = res.data;
      if (d.reachable) toast.success(`ThingsBoard reachable (${d.status_code})`);
      else toast.error(`ThingsBoard unreachable: ${d.error}`);
    },
    onError: (err) => toast.error('Test failed'),
  });

  // Raw poller log
  const { data: rawLogData } = useQuery({
    queryKey: ['silica-log'],
    queryFn: async () => (await axios.get(`${API}/silica/log`)).data,
    refetchInterval: 10000,
  });
  const rawLog = rawLogData?.entries || [];

  const ps = pollerStatus || {};
  const devices = devicesData?.devices || [];
  const shocks = shockData?.events || [];

  const formatTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="silica-data-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="silica-page-title">
            SILICA IoT Data
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live telemetry feed from the SODAQ SILICA platform
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => pollNow.mutate()}
          disabled={pollNow.isPending || !ps.running}
          data-testid="poll-now-btn"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${pollNow.isPending ? 'animate-spin' : ''}`} />
          Poll Now
        </Button>
      </div>

      {/* Poller Status Card */}
      <Card data-testid="poller-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4" />
            Poller Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Status</span>
                <span className="flex items-center font-medium" data-testid="poller-running-status">
                  <StatusDot status={ps.last_poll_status} />
                  {ps.running ? 'Running' : 'Stopped'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Last Poll</span>
                <span className="font-medium" data-testid="poller-last-poll">{ps.last_poll_status || '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Poll Interval</span>
                <span className="font-medium">{ps.poll_interval_seconds ? `${ps.poll_interval_seconds}s` : '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Total Polls</span>
                <span className="font-medium" data-testid="poller-total-polls">{ps.total_polls ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Devices Found</span>
                <span className="font-medium">{ps.devices_discovered ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Readings Processed</span>
                <span className="font-medium">{ps.telemetry_processed ?? '—'}</span>
              </div>
              {ps.last_error && (
                <div className="col-span-full">
                  <span className="text-muted-foreground block text-xs mb-1">Last Error</span>
                  <span className="text-amber-600 text-xs font-mono bg-amber-50 px-2 py-1 rounded" data-testid="poller-error">
                    {ps.last_error}
                  </span>
                </div>
              )}
              {ps.last_poll_time && (
                <div className="col-span-full">
                  <span className="text-muted-foreground block text-xs mb-1">Last Poll Time</span>
                  <span className="text-xs font-mono">{formatTime(ps.last_poll_time)}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw Poller Log */}
      <Card data-testid="raw-log-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Raw Poll Log ({rawLog.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-950 rounded-lg p-3 max-h-[360px] overflow-y-auto font-mono text-[11px] leading-relaxed" data-testid="raw-log-output">
            {rawLog.length === 0 ? (
              <span className="text-slate-500">No log entries yet. Waiting for first poll...</span>
            ) : (
              rawLog.slice().reverse().map((entry, i) => {
                const levelColor = {
                  INFO: 'text-emerald-400',
                  WARNING: 'text-amber-400',
                  ERROR: 'text-red-400',
                  DEBUG: 'text-slate-400',
                }[entry.level] || 'text-slate-400';
                const ts = entry.ts ? new Date(entry.ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                return (
                  <div key={`${entry.ts}-${entry.level}-${i}`} className="py-0.5 border-b border-slate-800/50 last:border-0">
                    <span className="text-slate-500 mr-2">{ts}</span>
                    <span className={`${levelColor} mr-2 font-bold`}>[{entry.level}]</span>
                    <span className="text-slate-200">{entry.msg}</span>
                    {entry.data && (
                      <pre className="text-slate-500 mt-0.5 ml-14 whitespace-pre-wrap break-all text-[10px]">
                        {JSON.stringify(entry.data, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* ThingsBoard Forwarder Status */}
      {tbStatus && (
        <Card data-testid="tb-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2"><Send className="h-4 w-4" />ThingsBoard Forwarder</span>
              <Button variant="outline" size="sm" onClick={() => tbTest.mutate()} disabled={tbTest.isPending} data-testid="tb-test-btn" className="gap-1.5 text-xs">
                <Plug className="h-3.5 w-3.5" />{tbTest.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Status</span>
                <span className="flex items-center font-medium" data-testid="tb-running-status">
                  <StatusDot status={tbStatus.running ? 'success' : tbStatus.enabled ? 'error' : 'not_started'} />
                  {tbStatus.running ? 'Running' : tbStatus.enabled ? 'Error' : 'Disabled'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Target</span>
                <span className="font-mono text-xs">{tbStatus.url || '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Forwarded</span>
                <span className="font-medium">{tbStatus.forwarded_count ?? 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Failed</span>
                <span className="font-medium text-red-600">{tbStatus.failed_count ?? 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Queue</span>
                <span className="font-medium">{tbStatus.queue_size ?? 0} / {tbStatus.queue_max ?? 500}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs mb-1">Credentials</span>
                <span className="font-medium">
                  {tbStatus.has_default_token ? 'Token' : tbStatus.has_tenant_credentials ? 'Tenant' : <span className="text-amber-600">Not set</span>}
                </span>
              </div>
              {tbStatus.last_error && (
                <div className="col-span-full">
                  <span className="text-muted-foreground block text-xs mb-1">Last Error</span>
                  <span className="text-amber-600 text-xs font-mono bg-amber-50 px-2 py-1 rounded">{tbStatus.last_error}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ThingsBoard Last Forwarded Payloads (JSON) */}
      {tbStatus?.last_payloads?.length > 0 && (
        <Card data-testid="tb-last-payloads-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-emerald-600" />
              Siste sendte payloads til ThingsBoard
              <Badge variant="secondary">{tbStatus.last_payloads.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {tbStatus.last_payloads.slice().reverse().map((p, i) => {
              // Support both telemetry as dict and as [{ts, values}] array
              const vals = Array.isArray(p.telemetry)
                ? (p.telemetry[0]?.values || {})
                : (p.telemetry || {});
              const deviceLabel = vals.shadowName || p.device_name || p.imei || '—';
              const deviceSub   = p.device_name || (p.imei ? `cm-spgw-${p.imei}` : '');
              return (
              <div key={i} className="rounded-lg border bg-slate-950 overflow-hidden" data-testid={`tb-payload-${i}`}>
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-300 font-semibold">{deviceLabel}</span>
                    {deviceSub && deviceLabel !== deviceSub && (
                      <span className="text-xs font-mono text-slate-500">{deviceSub}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">
                    {vals.lastUpdate ? new Date(vals.lastUpdate).toLocaleString('no-NO') : '—'}
                  </span>
                </div>
                <pre className="text-xs font-mono text-emerald-400 px-3 py-2 overflow-x-auto whitespace-pre-wrap leading-relaxed">
                  {JSON.stringify(vals, null, 2)}
                </pre>
              </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-devices">
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Wifi className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{devices.length}</p>
              <p className="text-xs text-muted-foreground">Devices</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-telemetry">
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Signal className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{telemetry.length}</p>
              <p className="text-xs text-muted-foreground">Recent Readings</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-shocks">
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Zap className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{shocks.length}</p>
              <p className="text-xs text-muted-foreground">Shock Events</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-alarms">
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{alarms.length}</p>
              <p className="text-xs text-muted-foreground">Alarms</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two-column: Devices + Shock Events */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Devices */}
        <Card data-testid="devices-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wifi className="h-4 w-4" /> Devices ({devices.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-y-auto">
            {devicesLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : devices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No devices found</p>
            ) : (
              <div className="space-y-2">
                {devices.map((d) => (
                  <div
                    key={d.id || d.device_id}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setExpandedDevice(expandedDevice === d.device_id ? null : d.device_id)}
                    data-testid={`device-row-${d.device_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {d.status === 'active' ? (
                          <Wifi className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <WifiOff className="h-4 w-4 text-slate-400" />
                        )}
                        <span className="font-medium text-sm">{d.name || d.device_id}</span>
                        {d.shadow_name && (
                          <span className="text-xs text-muted-foreground font-mono bg-slate-100 px-1.5 py-0.5 rounded" data-testid="shadow-name">
                            {d.shadow_name}
                          </span>
                        )}
                        <Badge variant="outline" className="text-[10px] px-1.5">{d.device_id}</Badge>
                      </div>
                      {expandedDevice === d.device_id ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    {expandedDevice === d.device_id && (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs border-t pt-3">
                        <div className="flex items-center gap-1.5">
                          <Thermometer className="h-3 w-3 text-blue-500" />
                          <span className="text-muted-foreground">Temp:</span>
                          <span className="font-medium">
                            {d.last_temperature != null ? `${d.last_temperature}°C` : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Battery className="h-3 w-3 text-green-500" />
                          <span className="text-muted-foreground">Battery:</span>
                          <span className="font-medium">
                            {d.battery_mv != null ? `${d.battery_mv}mV (~${d.battery_level != null ? Math.round(d.battery_level) : '?'}%)` : d.battery_level != null ? `~${Math.round(d.battery_level)}%` : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3 text-red-500" />
                          <span className="text-muted-foreground">Location:</span>
                          <span className="font-medium">
                            {d.last_latitude ? `${parseFloat(d.last_latitude).toFixed(4)}, ${parseFloat(d.last_longitude).toFixed(4)}` : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3 text-slate-500" />
                          <span className="text-muted-foreground">Seen:</span>
                          <span className="font-medium">{formatTime(d.last_seen)}</span>
                        </div>
                        {d.firmware_version && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">FW:</span>
                            <span className="font-mono font-medium">{d.firmware_version}</span>
                          </div>
                        )}
                        {d.grai && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">GRAI:</span>
                            <span className="font-mono font-medium text-[10px]">{d.grai}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shock Events */}
        <Card data-testid="shock-events-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4" /> Shock Events ({shocks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-y-auto">
            {shocksLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : shocks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No shock events recorded</p>
            ) : (
              <div className="space-y-2">
                {shocks.map((s) => (
                  <div key={s.id} className="border rounded-lg p-3" data-testid={`shock-event-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className={`h-4 w-4 ${s.max_g >= 5 ? 'text-red-500' : s.max_g >= 3 ? 'text-amber-500' : 'text-yellow-500'}`} />
                        <span className="font-bold text-sm">{s.max_g.toFixed(2)}g</span>
                        {s.axis && <Badge variant="outline" className="text-[10px]">Axis: {s.axis}</Badge>}
                        <Badge variant={s.max_g >= 3 ? 'destructive' : 'secondary'} className="text-[10px]">
                          {s.max_g >= 5 ? 'CRITICAL' : s.max_g >= 3 ? 'WARNING' : 'Minor'}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Device: <strong>{s.device_imei}</strong></span>
                      <span>{formatTime(s.timestamp)}</span>
                      {s.shipment_id && <span>Shipment: {s.shipment_id.slice(0, 8)}...</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Telemetry Feed */}
      <Card data-testid="telemetry-feed-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Signal className="h-4 w-4" /> Recent Telemetry ({telemetry.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {telemetryLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : telemetry.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No telemetry data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="telemetry-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 pr-3 font-medium">Device</th>
                    <th className="text-left py-2 pr-3 font-medium">Source</th>
                    <th className="text-right py-2 pr-3 font-medium">Temp</th>
                    <th className="text-right py-2 pr-3 font-medium">Battery</th>
                    <th className="text-left py-2 pr-3 font-medium">Location</th>
                    <th className="text-left py-2 pr-3 font-medium">Network</th>
                    <th className="text-center py-2 pr-3 font-medium">Motion</th>
                    <th className="text-left py-2 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {telemetry.slice(0, 25).map((t, i) => (
                    <tr key={t.id || i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 pr-3 font-mono font-medium">{t.device_id}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[10px]">
                          {t.connector_id === 'silica_poller' ? 'Poller' : t.connector_id === 'silica' ? 'Webhook' : t.connector_id}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium">
                        {t.temperature != null ? (
                          <span className={Number(t.temperature) > 8 ? 'text-red-600' : Number(t.temperature) < -25 ? 'text-blue-600' : ''}>
                            {Number(t.temperature).toFixed(1)}°C
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {t.battery_mv != null ? (
                          <span>
                            <span className="font-medium">{t.battery_mv}mV</span>
                            <span className="text-muted-foreground ml-1">~{t.battery_level != null ? `${Math.round(t.battery_level)}%` : '?'}</span>
                          </span>
                        ) : t.battery_level != null ? `~${Math.round(t.battery_level)}%` : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {t.location?.lat ? `${parseFloat(t.location.lat).toFixed(3)}, ${parseFloat(t.location.lng ?? t.location.lon ?? 0).toFixed(3)}` : '—'}
                      </td>
                      <td className="py-2 pr-3" data-testid={`telemetry-network-${i}`}>
                        <Badge variant="outline" className="text-[10px] font-mono">
                          {t.connectivity_type || '—'}
                        </Badge>
                        {t.signal_strength != null && (
                          <span className="text-muted-foreground ml-1.5">{t.signal_strength} dBm</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {t.motion_detected ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Moving</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Static</Badge>
                        )}
                      </td>
                      <td className="py-2 text-muted-foreground">{formatTime(t.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SilicaData;
